import { RefObject, useEffect, useRef } from "react";
import { useStateful } from "~/src/hooks/useStateful";

interface props {
  containerRef: RefObject<HTMLDivElement>;
  navbarRef: RefObject<HTMLDivElement>;
  staticShadow?: boolean;
}

//custom shadows (top, bottom) that indicates to the user that they can scroll up/down
//some pages have a static bottom shadow, specified in the useRouteProps
export default function useScrollShadow({ containerRef, navbarRef, staticShadow }: props) {
  const showShadow = useStateful<boolean>(false);
  const showShadowTop = useStateful<boolean>(false);

  const handleScroll = () => {
    const container = containerRef.current;
    if (!container) return;
    const scrollable = container.scrollHeight > container.clientHeight;
    const atBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 1;
    const atTop = container.scrollTop < 5;
    showShadow.set((scrollable && !atBottom) || (staticShadow ?? false));
    showShadowTop.set(scrollable && !atTop);
  };

  const shadowRef = useRef<HTMLDivElement>(null);
  const position = useStateful(0);

  const updatePosition = () => {
    //bottom shadow is postitioned relative to bottom bars, so that it appears to be above them
    const target = document.getElementById("bottom-bar");

    if (target) {
      const rect = target.getBoundingClientRect();
      position.set(rect.top - 8);
    } else if (navbarRef.current) {
      const rect = navbarRef.current.getBoundingClientRect();
      position.set(rect.top - 8);
    } else {
      position.set(window.innerHeight - 8);
    }
  };

  useEffect(() => {
    updatePosition();
    window.addEventListener("resize", updatePosition);
    const bottomBar = document.getElementById("bottom-bar") ?? document.getElementById("navbar");
    let observer: ResizeObserver | null = null;
    if (bottomBar) {
      observer = new ResizeObserver(() => updatePosition());
      observer.observe(bottomBar);
    }
    return () => {
      window.removeEventListener("resize", updatePosition);
      if (observer && bottomBar) observer.unobserve(bottomBar);
    };
  }, []);

  return {
    updatePosition,
    handleScroll,
    BottomShadow: (
      <div
        ref={shadowRef}
        className={`w-full max-w-244 absolute z-1 h-2 bg-linear-to-t from-ldir-brown-900/40 to-transparent transition-opacity duration-500 pointer-events-none ${
          showShadow.value ? "opacity-100" : "opacity-0"
        }`}
        style={{ top: position.value }}
      />
    ),
    TopShadow: <ShadowTop visible={showShadowTop.value} />,
  };
}

function ShadowTop({ visible }: { visible: boolean }) {
  const shadowRef = useRef<HTMLDivElement>(null);
  const position = useStateful(0);

  const updatePosition = () => {
    const header = document.getElementById("header");
    if (header) {
      const rect = header.getBoundingClientRect();
      position.set(rect.bottom + window.scrollY);
    }
  };

  useEffect(() => {
    updatePosition();
    window.addEventListener("resize", updatePosition);
    const header = document.getElementById("header");
    let observer: ResizeObserver | null = null;
    if (header) {
      observer = new ResizeObserver(() => updatePosition());
      observer.observe(header);
    }
    return () => {
      window.removeEventListener("resize", updatePosition);
      if (observer && header) observer.unobserve(header);
    };
  }, []);

  return (
    <div
      ref={shadowRef}
      className={`w-full max-w-244 absolute z-40 h-2 bg-linear-to-b from-ldir-brown-900/40 to-transparent transition-opacity duration-500 pointer-events-none ${
        visible ? "opacity-100" : "opacity-0"
      }`}
      style={{ top: position.value }}
    />
  );
}
